import { Box, Divider, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconWorld } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import Switch from '../../../components/ui/inputs/Switch';
import SegmentedControl from '../../../components/ui/navigation/SegmentedControl';

export default function AccessibilitySection() {
  const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
  const setReducedMotion = usePreferencesStore((state) => state.setReducedMotion);
  const highContrast = usePreferencesStore((state) => state.highContrast);
  const setHighContrast = usePreferencesStore((state) => state.setHighContrast);
  const strongFocus = usePreferencesStore((state) => state.strongFocus);
  const setStrongFocus = usePreferencesStore((state) => state.setStrongFocus);
  const grayscaleMode = usePreferencesStore((state) => state.grayscaleMode);
  const setGrayscaleMode = usePreferencesStore((state) => state.setGrayscaleMode);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const setLineSpacing = usePreferencesStore((state) => state.setLineSpacing);
  const letterSpacing = usePreferencesStore((state) => state.letterSpacing);
  const setLetterSpacing = usePreferencesStore((state) => state.setLetterSpacing);
  const enableTooltips = usePreferencesStore((state) => state.enableTooltips);
  const setEnableTooltips = usePreferencesStore((state) => state.setEnableTooltips);
  const textToSpeech = usePreferencesStore((state) => state.textToSpeech);
  const setTextToSpeech = usePreferencesStore((state) => state.setTextToSpeech);
  const fontSize = usePreferencesStore((state) => state.fontSize);
  const setFontSize = usePreferencesStore((state) => state.setFontSize);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group gap="sm">
        <ThemeIcon size="lg" radius="md" variant="light" color="green">
          <IconWorld size={20} />
        </ThemeIcon>
        <Box>
          <Text fw={600}>Accessibility</Text>
          <Text size="xs" c="dimmed">
            Adjust accessibility settings
          </Text>
        </Box>
      </Group>

      <Divider />

      {/* Visual Preferences */}
      <Text size="sm" fw={500} c="dimmed">
        Visual
      </Text>

      <Switch
        label="Reduce motion"
        description="Minimize animations throughout the app"
        checked={reducedMotion}
        onChange={(e) => setReducedMotion(e.target.checked)}
      />

      <Switch
        label="High contrast"
        description="Boost contrast for improved readability"
        checked={highContrast}
        onChange={(e) => setHighContrast(e.target.checked)}
      />

      <Switch
        label="Grayscale mode"
        description="Render the interface in grayscale"
        checked={grayscaleMode}
        onChange={(e) => setGrayscaleMode(e.target.checked)}
      />

      <Switch
        label="Strong focus outlines"
        description="Always show outlines on focused elements"
        checked={strongFocus}
        onChange={(e) => setStrongFocus(e.target.checked)}
      />

      <Divider />

      {/* Typography */}
      <Text size="sm" fw={500} c="dimmed">
        Typography
      </Text>

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Font Size
        </Text>
        <SegmentedControl
          fullWidth
          activeGradient
          data={[
            { label: 'XS', value: 'xs' },
            { label: 'Small', value: 'sm' },
            { label: 'Regular', value: 'regular' },
            { label: 'Large', value: 'lg' },
            { label: 'XL', value: 'xl' },
          ]}
          value={fontSize}
          onChange={(value) => setFontSize(value as typeof fontSize)}
        />
      </Box>

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Line spacing
        </Text>
        <SegmentedControl
          fullWidth
          activeGradient
          data={[
            { label: 'Tight', value: 'tight' },
            { label: 'Regular', value: 'regular' },
            { label: 'Relaxed', value: 'relaxed' },
            { label: 'Loose', value: 'loose' },
          ]}
          value={lineSpacing}
          onChange={(value) => setLineSpacing(value as typeof lineSpacing)}
        />
      </Box>

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Letter spacing
        </Text>
        <SegmentedControl
          fullWidth
          activeGradient
          data={[
            { label: 'Normal', value: 'normal' },
            { label: 'Wide', value: 'wide' },
          ]}
          value={letterSpacing}
          onChange={(value) => setLetterSpacing(value as typeof letterSpacing)}
        />
      </Box>

      <Divider />

      {/* UI Preferences */}
      <Text size="sm" fw={500} c="dimmed">
        Interaction
      </Text>

      <Switch
        label="Enable tooltips"
        description="Show tooltips and contextual help on hover/focus"
        checked={enableTooltips}
        onChange={(e) => setEnableTooltips(e.target.checked)}
      />

      <Switch
        label="Text to speech"
        description="Allow components to narrate key actions via speech synthesis"
        checked={textToSpeech}
        onChange={(e) => setTextToSpeech(e.target.checked)}
      />
    </Stack>
  );
}
